import Grid from '@mui/material/Grid';
import { AutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/AutoCompleteVirtualize.tsx';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import {
  type InstanceProps,
  type InventoryAndContentState,
  type InventoryOptionPayload,
  TargetableInventoryOption,
} from '@openx/types/targeting/inventoryAndContent.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import type { AnyAction } from '@reduxjs/toolkit';
import { type Dispatch, type JSX, type SetStateAction, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTargetingContext } from '../../../../utils/context/TargetingContext.tsx';
import {
  clearSelectedInstance,
  setInventories,
  setSelectedInstance,
} from '../../../shared/InventoryAndContent/inventoryHelpers.ts';
import { InventoryAutoComplete } from '../InventoryAutoComplete/InventoryAutoComplete.tsx';

type InventoryProps = {
  targetingParams: InventoryAndContentState;
  dispatchInventory: (action: AnyAction) => void;
  dispatchInstance: (action: AnyAction) => void;
  inputInstanceValue: InstanceProps | null;
  setInputInstanceValue: Dispatch<SetStateAction<InstanceProps | null>>;
};

const isExcludingMode = (inventory: InventoryOptionPayload): boolean =>
  !inventory.includes.length && inventory.excludes.length > 0;

const getSelectedOption = (inventory: InventoryOptionPayload) =>
  isExcludingMode(inventory) ? TargetableInventoryOption.EXCLUDES : TargetableInventoryOption.INCLUDES;

export const Inventory = ({
  targetingParams,
  dispatchInventory,
  dispatchInstance,
  inputInstanceValue,
  setInputInstanceValue,
}: InventoryProps): JSX.Element => {
  const { t } = useTranslation();
  const { useAccountUid, useCurrentInstanceState } = useTargetingContext();
  const accountUid = useAccountUid();

  const { useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();

  const [selectedMarket, setSelectedMarket] = useState<string | null>(null);

  const { isBefInstance } = useCurrentInstanceState();

  const inventoryOption: TargetableInventoryOption = getSelectedOption(targetingParams.inventory);

  const isUserAllowed =
    isAllowed(isAllowedByCompiledAcl(ACL_TYPE.ROOT_WORKAS_ANYINSTANCE)) ||
    isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE)) ||
    isAllowed(isAllowedByCompiledAcl(ACL_TYPE.MARKET_OPERATOR_SEARCH));

  const onInventorySelect = useCallback(
    item => {
      dispatchInventory({
        payload: [...targetingParams.inventory[inventoryOption], item],
        type: setInventories[inventoryOption],
      } as unknown as AnyAction);
    },
    [dispatchInventory, inventoryOption, targetingParams.inventory]
  );

  const instanceChangeHandler = useCallback(
    (value: InstanceProps | null, reason: string) => {
      if (reason === 'clear') {
        setInputInstanceValue(null);
        dispatchInstance({
          type: clearSelectedInstance,
        } as unknown as AnyAction);
      } else {
        setInputInstanceValue(value);
        dispatchInstance({
          payload: value?.id,
          type: setSelectedInstance,
        } as unknown as AnyAction);
      }
    },
    [setInputInstanceValue, dispatchInstance]
  );

  const marketChangeHandler = useCallback(
    (id: string | null) => {
      setSelectedMarket(id);
      setInputInstanceValue(null);
    },
    [setInputInstanceValue]
  );

  return isUserAllowed && isBefInstance ? (
    <Grid container spacing={1}>
      <Grid size={6}>
        <InventoryMarkets
          onChange={(_event, value) => {
            marketChangeHandler(value?.id || null);
          }}
        />
      </Grid>

      <Grid size={6}>
        <InventoryInstance
          inputInstanceValue={inputInstanceValue}
          selectedMarket={selectedMarket}
          onChange={(_event, value, reason) => {
            instanceChangeHandler(value, reason);
          }}
        />
      </Grid>

      {inputInstanceValue && (
        <Grid size={12}>
          <InventoryAutoComplete
            onInventorySelect={onInventorySelect}
            targetingParams={targetingParams}
            testId="inventory-autocomplete-include"
            instanceUid={inputInstanceValue.id}
            accountId={accountUid}
            inputLabel={t('Ad Unit')}
          />
        </Grid>
      )}
    </Grid>
  ) : (
    <Grid container spacing={1}>
      <Grid size={8}>
        <InventoryAutoComplete
          onInventorySelect={onInventorySelect}
          targetingParams={targetingParams}
          testId="inventory-autocomplete-include"
          accountId={accountUid}
          inputLabel={t('Ad Unit')}
        />
      </Grid>
    </Grid>
  );
};

const InventoryMarkets = ({ onChange }) => {
  const { t } = useTranslation();
  const { useFetchInventoryMarket } = useTargetingContext();
  const { markets, loading } = useFetchInventoryMarket();

  if (markets && markets.length > 0) {
    return (
      <AutoCompleteVirtualize
        loading={loading}
        disabled={loading}
        disableClearable
        defaultValue={markets[0]}
        options={markets}
        getOptionLabel={option => option.name}
        textFieldProps={{
          label: t('Market'),
        }}
        data-test="inventory-autocomplete-market"
        onChange={onChange}
      />
    );
  }

  return null;
};

const InventoryInstance = ({ selectedMarket, inputInstanceValue, onChange }) => {
  const { t } = useTranslation();
  const { useFetchInventoryInstances } = useTargetingContext();
  const { instances, loading } = useFetchInventoryInstances(selectedMarket);

  return (
    <AutoCompleteVirtualize
      loading={loading}
      disabled={loading}
      textFieldProps={{
        label: t('Instance'),
        placeholder: t('Select Instance'),
      }}
      value={inputInstanceValue}
      options={instances}
      getOptionLabel={option => option.name}
      data-test="inventory-autocomplete-instance"
      onChange={onChange}
    />
  );
};
